/** @format */

import React from "react";
import { useTranslation } from "react-i18next";
import {
	Container,
	Box,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Divider,
	Chip,
	styled,
	alpha,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Link,
} from "@mui/material";
import {
	CreditCard,
	AccountBalance,
	Info,
	CheckCircle,
	ExpandMore,
	Payments,
	Receipt,
	WarningAmber,
	Schedule,
} from "@mui/icons-material";
import { Footer } from "../allComponents/AllComponents";

const PageContainer = styled(Container)(({ theme }) => ({
	paddingTop: theme.spacing(8),
	paddingBottom: theme.spacing(8),
	fontFamily: "'Inter', sans-serif",
}));

const Section = styled(Box)(({ theme }) => ({
	marginBottom: theme.spacing(6),
}));

const HeroSection = styled(Box)(({ theme }) => ({
	textAlign: "center",
	marginBottom: theme.spacing(6),
	padding: theme.spacing(4),
	background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
	borderRadius: theme.spacing(2),
}));

const FeeCard = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(3),
	borderRadius: theme.spacing(2),
	border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
	transition: "all 0.3s ease",
	height: "100%",
	"&:hover": {
		boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
		transform: "translateY(-4px)",
	},
}));

const IconWrapper = styled(Box)(({ theme }) => ({
	width: 56,
	height: 56,
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	marginBottom: theme.spacing(2),
	background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
	color: "#fff",
}));

const ExampleBox = styled(Box)(({ theme }) => ({
	backgroundColor: alpha(theme.palette.grey[100], 0.8),
	borderRadius: theme.spacing(1.5),
	padding: theme.spacing(3),
	marginTop: theme.spacing(2),
	border: `1px solid ${theme.palette.divider}`,
}));

const FlowStep = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	padding: theme.spacing(1.5, 0),
	"&:not(:last-child)": {
		borderBottom: `1px dashed ${theme.palette.divider}`,
	},
}));

const HighlightChip = styled(Chip)(({ theme }) => ({
	fontWeight: 600,
	fontSize: "0.875rem",
}));

const ComparisonTable = styled(TableContainer)(({ theme }) => ({
	borderRadius: theme.spacing(2),
	border: `1px solid ${theme.palette.divider}`,
	marginTop: theme.spacing(3),
	backgroundColor: theme.palette.background.paper,
	boxShadow: "none",
}));

interface DetailedRow {
	type: string;
	charger: string;
	fee: string;
	when: string;
}

interface AltRow {
	alternative: string;
	cost: string;
	apr: string;
}

interface FaqItem {
	question: string;
	answer: string;
}

interface Scenario {
	title: string;
	description: string;
}

const Fees = () => {
	const { t } = useTranslation("pages");

	const detailedRows = t("fees.breakdown.detailedRows", {
		returnObjects: true,
	}) as DetailedRow[];

	const altRows = t("fees.comparison.altRows", {
		returnObjects: true,
	}) as AltRow[];

	const faqItems = [
		{ key: "q1", ...((t("fees.faq.q1", { returnObjects: true }) as FaqItem)) },
		{ key: "q2", ...((t("fees.faq.q2", { returnObjects: true }) as FaqItem)) },
		{ key: "q3", ...((t("fees.faq.q3", { returnObjects: true }) as FaqItem)) },
		{ key: "q4", ...((t("fees.faq.q4", { returnObjects: true }) as FaqItem)) },
		{ key: "q5", ...((t("fees.faq.q5", { returnObjects: true }) as FaqItem)) },
		{ key: "q6", ...((t("fees.faq.q6", { returnObjects: true }) as FaqItem)) },
		{ key: "q7", ...((t("fees.faq.q7", { returnObjects: true }) as FaqItem)) },
	];

	const scenario1 = t("fees.examples.scenario1", { returnObjects: true }) as Scenario;
	const scenario2 = t("fees.examples.scenario2", { returnObjects: true }) as Scenario;
	const scenario3 = t("fees.examples.scenario3", { returnObjects: true }) as Scenario;

	return (
		<React.Fragment>
			<PageContainer maxWidth="md">
				{/* Hero Section */}
				<HeroSection>
					<Typography
						variant="h3"
						gutterBottom
						sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>
						{t("fees.hero.title")}
					</Typography>
					<Typography
						variant="h6"
						color="text.secondary"
						sx={{ maxWidth: 600, mx: "auto", mb: 3 }}>
						{t("fees.hero.subtitle")}
					</Typography>
					<Box
						sx={{
							maxWidth: 680,
							mx: "auto",
							mb: 3,
							display: "flex",
							alignItems: "flex-start",
							gap: 1.5,
							textAlign: "left",
							p: 2,
							borderRadius: 2,
							border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
							backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
						}}>
						<Info color="info" sx={{ mt: "2px", flexShrink: 0 }} />
						<Typography variant="body2" color="text.secondary">
							{t("fees.hero.productNote")}
						</Typography>
					</Box>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}>
						<HighlightChip
							icon={<CheckCircle />}
							label={t("fees.chips.contribution")}
							color="success"
							variant="outlined"
						/>
						<HighlightChip
							icon={<CheckCircle />}
							label={t("fees.chips.payout")}
							color="success"
							variant="outlined"
						/>
						<HighlightChip
							icon={<CheckCircle />}
							label={t("fees.chips.withdrawal")}
							color="success"
							variant="outlined"
						/>
					</Box>
				</HeroSection>

				{/* Fee Summary */}
				<Section>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
						{t("fees.summary.heading")}
					</Typography>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
							gap: 3,
						}}>
						{/* Contribution Fees Card */}
						<FeeCard elevation={0}>
							<IconWrapper>
								<CreditCard />
							</IconWrapper>
							<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
								{t("fees.summary.contribution.label")}
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								{t("fees.summary.contribution.when")}
							</Typography>
							<Divider sx={{ my: 2 }} />
							<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
								<Typography variant="body2" color="text.secondary">
									{t("fees.summary.contribution.stripeLabel")}
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									2.9% + $0.30
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
								<Typography variant="body2" color="text.secondary">
									{t("fees.summary.contribution.fundsyFiLabel")}
								</Typography>
								<Typography variant="body2" fontWeight={600} color="success.main">
									$0.00
								</Typography>
							</Box>
							<Chip
								size="small"
								label={t("fees.summary.contribution.chip")}
								color="info"
								variant="outlined"
								sx={{ fontSize: "0.75rem" }}
							/>
						</FeeCard>

						{/* Payout Fees Card */}
						<FeeCard elevation={0}>
							<IconWrapper>
								<AccountBalance />
							</IconWrapper>
							<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
								{t("fees.summary.payout.label")}
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								{t("fees.summary.payout.when")}
							</Typography>
							<Divider sx={{ my: 2 }} />
							<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
								<Typography variant="body2" color="text.secondary">
									{t("fees.summary.payout.stripeLabel")}
								</Typography>
								<Typography variant="body2" fontWeight={600} color="success.main">
									{t("fees.summary.payout.freeText")}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
								<Typography variant="body2" color="text.secondary">
									{t("fees.summary.contribution.fundsyFiLabel")}
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									2.5%
								</Typography>
							</Box>
							<Chip
								size="small"
								label={t("fees.summary.payout.chip")}
								color="warning"
								variant="outlined"
								sx={{ fontSize: "0.75rem" }}
							/>
						</FeeCard>

						{/* Late Fees Card */}
						<FeeCard elevation={0} sx={{ gridColumn: { md: "span 2" } }}>
							<IconWrapper
								sx={{
									background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
								}}>
								<WarningAmber />
							</IconWrapper>
							<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
								{t("fees.summary.lateFees.label")}
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								{t("fees.summary.lateFees.when")}
							</Typography>
							<Divider sx={{ my: 2 }} />
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
									gap: 2,
								}}>
								<Box>
									<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
										<Typography variant="body2" color="text.secondary">
											{t("fees.summary.lateFees.lateLabel")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="warning.dark">
											{t("fees.summary.lateFees.lateValue")}
										</Typography>
									</Box>
									<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
										<Typography variant="body2" color="text.secondary">
											{t("fees.summary.lateFees.graceLabel")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="success.main">
											{t("fees.summary.lateFees.graceValue")}
										</Typography>
									</Box>
								</Box>
								<Box
									sx={{
										backgroundColor: alpha("#f59e0b", 0.1),
										borderRadius: 1.5,
										p: 2,
										border: `1px solid ${alpha("#f59e0b", 0.3)}`,
									}}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
										<Schedule fontSize="small" sx={{ color: "#d97706" }} />
										<Typography variant="body2" fontWeight={600} color="warning.dark">
											{t("fees.summary.lateFees.howItWorks")}
										</Typography>
									</Box>
									<Typography variant="body2" color="text.secondary">
										{t("fees.summary.lateFees.howItWorksDesc")}
									</Typography>
								</Box>
							</Box>
							<Box sx={{ mt: 2 }}>
								<Chip
									size="small"
									label={t("fees.summary.lateFees.chip1")}
									color="warning"
									variant="outlined"
									sx={{ fontSize: "0.75rem", mr: 1 }}
								/>
								<Chip
									size="small"
									label={t("fees.summary.lateFees.chip2")}
									color="info"
									variant="outlined"
									sx={{ fontSize: "0.75rem" }}
								/>
							</Box>
						</FeeCard>
					</Box>
				</Section>

				{/* Detailed Fee Table */}
				<Section>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
						{t("fees.breakdown.heading")}
					</Typography>

					<ComparisonTable>
						<Table>
							<TableHead>
								<TableRow sx={{ backgroundColor: "grey.50" }}>
									<TableCell sx={{ fontWeight: 600 }}>
										{t("fees.breakdown.table.transactionType")}
									</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>
										{t("fees.breakdown.table.whoCharges")}
									</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>
										{t("fees.breakdown.table.fee")}
									</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>
										{t("fees.breakdown.table.whenApplied")}
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{Array.isArray(detailedRows) &&
									detailedRows.map((row, idx) => (
										<TableRow
											key={idx}
											sx={
												row.charger === "FundsyFi" && idx === 3
													? { backgroundColor: alpha("#4caf50", 0.05) }
													: row.charger === "FundsyFi" && idx === 4
														? { backgroundColor: alpha("#f59e0b", 0.05) }
														: {}
											}>
											<TableCell>
												<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
													{idx === 0 && <CreditCard fontSize="small" color="primary" />}
													{idx === 1 && <AccountBalance fontSize="small" color="primary" />}
													{idx === 2 && <Payments fontSize="small" color="secondary" />}
													{idx === 3 && <Receipt fontSize="small" color="success" />}
													{idx === 4 && (
														<WarningAmber fontSize="small" sx={{ color: "#d97706" }} />
													)}
													{idx === 3 ? <strong>{row.type}</strong> : row.type}
												</Box>
											</TableCell>
											<TableCell>
												<Chip
													label={row.charger}
													size="small"
													variant="outlined"
													color={row.charger === "FundsyFi" ? (idx === 4 ? "warning" : "primary") : "default"}
												/>
											</TableCell>
											<TableCell
												sx={{
													fontWeight: 600,
													color:
														row.fee === "FREE" || row.fee === "GRATIS" || row.fee === "GRATUIT"
															? "success.main"
															: idx === 4
																? "warning.dark"
																: "inherit",
												}}>
												{row.fee}
											</TableCell>
											<TableCell>{row.when}</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					</ComparisonTable>
				</Section>

				{/* Real Example Scenarios */}
				<Section>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
						{t("fees.examples.heading")}
					</Typography>
					<Typography variant="body2" color="text.secondary" paragraph>
						{t("fees.examples.subtitle")}
					</Typography>

					{/* Scenario 1 */}
					<Accordion
						defaultExpanded
						elevation={0}
						sx={{
							border: 1,
							borderColor: "divider",
							borderRadius: 2,
							mb: 2,
							"&:before": { display: "none" },
						}}>
						<AccordionSummary expandIcon={<ExpandMore />}>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 600 }}>
									{scenario1.title}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{scenario1.description}
								</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
									gap: 3,
								}}>
								<ExampleBox>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										gutterBottom
										sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<CreditCard color="primary" /> {t("fees.examples.calc.eachContribution")}
									</Typography>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.yourContribution")}
										</Typography>
										<Typography variant="body2" fontWeight={600}>$10.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.stripeFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="error.main">+$0.59</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
											{t("fees.examples.calc.youPay")}
										</Typography>
										<Typography variant="body2" fontWeight={700} color="primary.main">$10.59</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.addedToPool")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="success.main">$10.00</Typography>
									</FlowStep>
								</ExampleBox>

								<ExampleBox>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										gutterBottom
										sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<AccountBalance color="primary" /> {t("fees.examples.calc.yourPayout")} (After 4 Weeks)
									</Typography>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.poolTotal")} (10 × $10 × 4)
										</Typography>
										<Typography variant="body2" fontWeight={600}>$400.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.fundsyFiFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="error.main">−$10.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.stripePayoutFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="success.main">$0.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
											{t("fees.examples.calc.youReceive")}
										</Typography>
										<Typography variant="body2" fontWeight={700} color="primary.main">$390.00</Typography>
									</FlowStep>
								</ExampleBox>
							</Box>

							<Box sx={{ mt: 3, p: 2, backgroundColor: alpha("#4caf50", 0.1), borderRadius: 2 }}>
								<Typography variant="subtitle2" fontWeight={600} gutterBottom>
									{t("fees.examples.calc.totalCostSummary")}
								</Typography>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.stripeFees")} (4 contributions)
										</Typography>
										<Typography variant="h6" fontWeight={600}>$2.36</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.fundsyFiPayoutFee")}
										</Typography>
										<Typography variant="h6" fontWeight={600}>$10.00</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.totalFeesPaid")}
										</Typography>
										<Typography variant="h6" fontWeight={600}>$12.36</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.effectiveCost")}
										</Typography>
										<Typography variant="h6" fontWeight={600} color="success.main">3.1%</Typography>
									</Box>
								</Box>
							</Box>
						</AccordionDetails>
					</Accordion>

					{/* Scenario 2 */}
					<Accordion
						elevation={0}
						sx={{
							border: 1,
							borderColor: "divider",
							borderRadius: 2,
							mb: 2,
							"&:before": { display: "none" },
						}}>
						<AccordionSummary expandIcon={<ExpandMore />}>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 600 }}>
									{scenario2.title}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{scenario2.description}
								</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
									gap: 3,
								}}>
								<ExampleBox>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										gutterBottom
										sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<CreditCard color="primary" /> {t("fees.examples.calc.eachContribution")}
									</Typography>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.yourContribution")}
										</Typography>
										<Typography variant="body2" fontWeight={600}>$100.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.stripeFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="error.main">+$3.20</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
											{t("fees.examples.calc.youPay")}
										</Typography>
										<Typography variant="body2" fontWeight={700} color="primary.main">$103.20</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.addedToPool")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="success.main">$100.00</Typography>
									</FlowStep>
								</ExampleBox>

								<ExampleBox>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										gutterBottom
										sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<AccountBalance color="primary" /> {t("fees.examples.calc.yourPayout")}
									</Typography>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.poolTotal")} (8 × $100)
										</Typography>
										<Typography variant="body2" fontWeight={600}>$800.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.fundsyFiFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="error.main">−$20.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.stripePayoutFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="success.main">$0.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
											{t("fees.examples.calc.youReceive")}
										</Typography>
										<Typography variant="body2" fontWeight={700} color="primary.main">$780.00</Typography>
									</FlowStep>
								</ExampleBox>
							</Box>

							<Box sx={{ mt: 3, p: 2, backgroundColor: alpha("#4caf50", 0.1), borderRadius: 2 }}>
								<Typography variant="subtitle2" fontWeight={600} gutterBottom>
									{t("fees.examples.calc.totalCostSummary")}
								</Typography>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.stripeFees")} (8 contributions)
										</Typography>
										<Typography variant="h6" fontWeight={600}>$25.60</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.fundsyFiPayoutFee")}
										</Typography>
										<Typography variant="h6" fontWeight={600}>$20.00</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.totalFeesPaid")}
										</Typography>
										<Typography variant="h6" fontWeight={600}>$45.60</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.effectiveCost")}
										</Typography>
										<Typography variant="h6" fontWeight={600} color="success.main">5.7%</Typography>
									</Box>
								</Box>
							</Box>
						</AccordionDetails>
					</Accordion>

					{/* Scenario 3 */}
					<Accordion
						elevation={0}
						sx={{
							border: 1,
							borderColor: "divider",
							borderRadius: 2,
							"&:before": { display: "none" },
						}}>
						<AccordionSummary expandIcon={<ExpandMore />}>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 600 }}>
									{scenario3.title}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{scenario3.description}
								</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
									gap: 3,
								}}>
								<ExampleBox>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										gutterBottom
										sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<CreditCard color="primary" /> {t("fees.examples.calc.eachContribution")}
									</Typography>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.yourContribution")}
										</Typography>
										<Typography variant="body2" fontWeight={600}>$500.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.stripeFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="error.main">+$14.80</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
											{t("fees.examples.calc.youPay")}
										</Typography>
										<Typography variant="body2" fontWeight={700} color="primary.main">$514.80</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.addedToPool")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="success.main">$500.00</Typography>
									</FlowStep>
								</ExampleBox>

								<ExampleBox>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										gutterBottom
										sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<AccountBalance color="primary" /> {t("fees.examples.calc.yourPayout")}
									</Typography>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.poolTotal")} (5 × $500)
										</Typography>
										<Typography variant="body2" fontWeight={600}>$2,500.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.fundsyFiFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="error.main">−$62.50</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1 }}>
											{t("fees.examples.calc.stripePayoutFee")}
										</Typography>
										<Typography variant="body2" fontWeight={600} color="success.main">$0.00</Typography>
									</FlowStep>
									<FlowStep>
										<Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
											{t("fees.examples.calc.youReceive")}
										</Typography>
										<Typography variant="body2" fontWeight={700} color="primary.main">$2,437.50</Typography>
									</FlowStep>
								</ExampleBox>
							</Box>

							<Box sx={{ mt: 3, p: 2, backgroundColor: alpha("#4caf50", 0.1), borderRadius: 2 }}>
								<Typography variant="subtitle2" fontWeight={600} gutterBottom>
									{t("fees.examples.calc.totalCostSummary")}
								</Typography>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.stripeFees")} (5 contributions)
										</Typography>
										<Typography variant="h6" fontWeight={600}>$74.00</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.fundsyFiPayoutFee")}
										</Typography>
										<Typography variant="h6" fontWeight={600}>$62.50</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.totalFeesPaid")}
										</Typography>
										<Typography variant="h6" fontWeight={600}>$136.50</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t("fees.examples.calc.effectiveCost")}
										</Typography>
										<Typography variant="h6" fontWeight={600} color="success.main">5.5%</Typography>
									</Box>
								</Box>
							</Box>
						</AccordionDetails>
					</Accordion>
				</Section>

				{/* Comparison Section */}
				<Section>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
						{t("fees.comparison.heading")}
					</Typography>
					<Typography variant="body2" color="text.secondary" paragraph>
						{t("fees.comparison.subtitle")}
					</Typography>

					<ComparisonTable>
						<Table>
							<TableHead>
								<TableRow sx={{ backgroundColor: "grey.50" }}>
									<TableCell sx={{ fontWeight: 600 }}>
										{t("fees.comparison.altTable.alternative")}
									</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>
										{t("fees.comparison.altTable.costToAccess")}
									</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>
										{t("fees.comparison.altTable.aprEquivalent")}
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{Array.isArray(altRows) &&
									altRows.map((row, idx) => (
										<TableRow
											key={idx}
											sx={idx === 0 ? { backgroundColor: alpha("#4caf50", 0.08) } : {}}>
											<TableCell>
												{idx === 0 ? (
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<CheckCircle color="success" fontSize="small" />
														<strong>{row.alternative}</strong>
													</Box>
												) : (
													row.alternative
												)}
											</TableCell>
											<TableCell
												sx={idx === 0 ? { fontWeight: 700, color: "success.main" } : {}}>
												{row.cost}
											</TableCell>
											<TableCell
												sx={
													idx === 0
														? { fontWeight: 700, color: "success.main" }
														: idx < altRows.length - 1
															? { color: "error.main" }
															: { color: "warning.main" }
												}>
												{row.apr}
											</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					</ComparisonTable>
				</Section>

				{/* FAQ Section */}
				<Section>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
						{t("fees.faq.heading")}
					</Typography>

					{faqItems.map((item) => (
						<Accordion
							key={item.key}
							elevation={0}
							sx={{
								border: 1,
								borderColor: "divider",
								mb: 1,
								"&:before": { display: "none" },
							}}>
							<AccordionSummary expandIcon={<ExpandMore />}>
								<Typography fontWeight={600}>{item.question}</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Typography variant="body2" color="text.secondary">
									{item.answer}
								</Typography>
							</AccordionDetails>
						</Accordion>
					))}
				</Section>

				{/* Contact Section */}
				<Section>
					<Box
						sx={{
							textAlign: "center",
							p: 4,
							backgroundColor: "grey.50",
							borderRadius: 2,
						}}>
						<Info color="primary" sx={{ fontSize: 40, mb: 2 }} />
						<Typography variant="h6" gutterBottom fontWeight={600}>
							{t("fees.contact.heading")}
						</Typography>
						<Typography variant="body2" color="text.secondary" paragraph>
							{t("fees.contact.description")}
						</Typography>
						<Link href={`mailto:${t("fees.contact.email")}`} sx={{ fontWeight: 600 }}>
							{t("fees.contact.email")}
						</Link>
					</Box>
				</Section>
			</PageContainer>
			<Footer />
		</React.Fragment>
	);
};

export default Fees;
